import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSidebarNavItem,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { getItem, postItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert } from "../utils/messages";

interface DeveloperMode {
  enabled: boolean;
  limitDes: number | null;
  limitReceipts: number | null;
  expiresAt: string | null;
}

interface DemoDeveloperModeNavItemProps {
  taxPayerId: number;
}

const developerModeCache = new Map<number, DeveloperMode>();
const pendingDeveloperModes = new Map<number, Promise<DeveloperMode | null>>();

const fetchDeveloperMode = (taxPayerId: number, refresh = false): Promise<DeveloperMode | null> => {
  const cached = developerModeCache.get(taxPayerId);
  if (cached && !refresh) return Promise.resolve(cached);

  const pending = pendingDeveloperModes.get(taxPayerId);
  if (pending) return pending;

  const request = getItem<DeveloperMode>(`/tax_payers/${taxPayerId}/developer_mode/`)
    .then((status) => {
      if (status.status !== SUCCESS || status.data === undefined) return null;
      developerModeCache.set(taxPayerId, status.data);
      return status.data;
    })
    .catch(() => null)
    .finally(() => pendingDeveloperModes.delete(taxPayerId));
  pendingDeveloperModes.set(taxPayerId, request);
  return request;
};

const DemoDeveloperModeNavItem = (props: DemoDeveloperModeNavItemProps) => {
  const { taxPayerId } = props;

  const [developerMode, setDeveloperMode] = useState<DeveloperMode | null>(
    () => developerModeCache.get(taxPayerId) ?? null,
  );
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchDeveloperMode(taxPayerId).then((mode) => {
      if (mounted && mode) setDeveloperMode(mode);
    });
    return () => { mounted = false; };
  }, [taxPayerId]);

  const onEnableClick = async () => {
    setSubmitting(true);
    const status = await postItem<{}>(
      `/tax_payers/${taxPayerId}/developer_mode/`,
      {},
    );

    if (status.status !== SUCCESS) {
      let message = "Ha ocurrido un error!!";
      if (status.detail !== undefined) {
        message = status.detail;
      }
      errorAlert(message);
    } else {
      successAlert("Modo desarrollador activado");
      setShowModal(false);
      const enabledMode = { ...developerMode!, enabled: true };
      developerModeCache.set(taxPayerId, enabledMode);
      setDeveloperMode(enabledMode);
      const refreshedMode = await fetchDeveloperMode(taxPayerId, true);
      if (refreshedMode) setDeveloperMode(refreshedMode);
    }

    setSubmitting(false);
  };

  if (developerMode === null) {
    return null;
  }

  if (developerMode.enabled) {
    return (
      <CSidebarNavItem
        name="Modo desarrollador"
        icon={<i className="c-sidebar-nav-icon fa-solid fa-code" />}
        badge={{ color: "success", text: "Activo" }}
        disabled
        aria-disabled="true"
        title="Modo desarrollador activo"
      />
    );
  }

  return (
    <>
      <CSidebarNavItem
        name="Modo desarrollador"
        icon={<i className="c-sidebar-nav-icon fa-solid fa-code" />}
        onClick={() => setShowModal(true)}
      />
      {createPortal(
        <CModal show={showModal} onClose={() => setShowModal(false)}>
          <CModalHeader closeButton>
            <CModalTitle>Modo desarrollador</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <p style={{ margin: "0 0 12px 0" }}>
              Activá esta opción si vas a integrar tu sistema con la API.
            </p>
            <p style={{ margin: "0" }}>
              Si no estás integrando, no hace falta que la actives.
            </p>
          </CModalBody>
          <CModalFooter>
            <CButton
              color="secondary"
              disabled={submitting}
              onClick={() => setShowModal(false)}
            >
              Cancelar
            </CButton>
            <CButton
              color="primary"
              disabled={submitting}
              onClick={onEnableClick}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Activando..." : "Activar"}
            </CButton>
          </CModalFooter>
        </CModal>,
        document.body,
      )}
    </>
  );
};

export default DemoDeveloperModeNavItem;
